/**
 * "For a while" moves: a person dropped on another step can go there for part
 * of their block and come back. Pure helpers for the drop prompt — plant-local
 * ISO strings without a zone ("2026-10-13T07:00:00"), minutes counted from the
 * block's own day so a night block past midnight keeps counting up.
 */

/** Shortest lend, in minutes (the server enforces the same). */
export const MIN_LEND_MINUTES = 120;

const DAY = 24 * 60;

/** Minutes of `iso` from midnight of `dayIso`'s date. */
export function minutesFrom(dayIso: string, iso: string): number {
	const days = Math.round((Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${dayIso.slice(0, 10)}T00:00:00Z`)) / 86400000);
	return days * DAY + Number(iso.slice(11, 13)) * 60 + Number(iso.slice(14, 16));
}

/** "HH:mm" of a minute count (wraps past midnight). */
export function clockOf(minutes: number): string {
	const m = ((minutes % DAY) + DAY) % DAY;
	return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Plant-local ISO for a minute count from midnight of `dayIso`'s date. */
export function isoOf(dayIso: string, minutes: number): string {
	const date = new Date(Date.parse(`${dayIso.slice(0, 10)}T00:00:00Z`) + Math.floor(minutes / DAY) * 86400000);
	return `${date.toISOString().slice(0, 10)}T${clockOf(minutes)}:00`;
}

/** Whether a block is long enough to lend part of it (otherwise the drop moves it whole). */
export function canLend(startIso: string, endIso: string): boolean {
	return minutesFrom(startIso, endIso) - minutesFrom(startIso, startIso) > MIN_LEND_MINUTES;
}

/**
 * Where the lend starts by default: when the target step starts inside the
 * block, then; else the block's start. Rounded to the quarter hour and pulled
 * back so two hours still fit.
 */
export function defaultLendStart(startIso: string, endIso: string, stepStartIso?: string | null): number {
	const start = minutesFrom(startIso, startIso);
	const end = minutesFrom(startIso, endIso);
	const step = stepStartIso ? minutesFrom(startIso, stepStartIso) : start;
	const wanted = Math.round(Math.max(start, Math.min(step, end)) / 15) * 15;
	return Math.max(start, Math.min(wanted, end - MIN_LEND_MINUTES));
}

/** Lengths offered from `from`: whole hours from 2 up to what is left, then "rest of the block". */
export function lendLengths(startIso: string, endIso: string, from: number): number[] {
	const left = minutesFrom(startIso, endIso) - from;
	const lengths: number[] = [];
	for (let m = MIN_LEND_MINUTES; m < left; m += 60) lengths.push(m);
	if (left >= MIN_LEND_MINUTES) lengths.push(left);
	return lengths;
}

/** Why a lend from `from` for `length` minutes cannot be sent, or null. */
export function lendProblem(startIso: string, endIso: string, from: number, length: number): string | null {
	const start = minutesFrom(startIso, startIso);
	const end = minutesFrom(startIso, endIso);
	if (from < start || from >= end) return `Pick a time between ${clockOf(start)} and ${clockOf(end - MIN_LEND_MINUTES)}.`;
	if (length < MIN_LEND_MINUTES) return `At least ${MIN_LEND_MINUTES / 60} hours.`;
	if (from + length > end) return `Their block ends at ${clockOf(end)}; pick an earlier time or a shorter while.`;
	return null;
}

/** "2 h", "2 h 30 min". */
export function lengthLabel(minutes: number): string {
	return minutes % 60 === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
